/**
 * Generates a random state string for OAuth2.
 * This is used to prevent CSRF attacks.
 *
 * @see https://auth0.com/docs/protocols/oauth2/oauth-state
 */
export function generateState() {
  return Math.random().toString(36).substring(2);
}

/**
 * Creates an OAuth2 authorization URL for Keycloak.
 * This URL is used to redirect the user to the Keycloak login page.
 *
 * @param clientId - The Keycloak client ID.
 * @param state - The state string generated by `generateState`.
 * @param redirectUri - The URL to redirect the user back to after login.
 * @param authority - The Keycloak authority URL.
 *
 * @see https://www.oauth.com/oauth2-servers/authorization/the-authorization-request/
 */
export function createKeycloakAuthUrl(inputs: {
  clientId: string;
  state: string;
  redirectUri: string;
  authority: string;
}) {
  const authorizationUrl = new URL(
    `${inputs.authority}/protocol/openid-connect/auth`,
  );

  authorizationUrl.searchParams.append("response_type", "code");
  authorizationUrl.searchParams.append("client_id", inputs.clientId);
  authorizationUrl.searchParams.append("redirect_uri", inputs.redirectUri);
  authorizationUrl.searchParams.append("state", inputs.state);
  authorizationUrl.searchParams.append("scope", "openid profile email");
  authorizationUrl.searchParams.append("app", "true");

  return authorizationUrl.toString();
}

export function isRedirectUrlMatching(url1: string, url2?: string) {
  if (url2 !== undefined) {
    const uri1 = new URL(url1, "http://test");
    const uri2 = new URL(url2, "http://test");

    if (uri2.pathname !== "" && uri2.pathname !== "/") {
      if (!uri1.pathname.includes(uri2.pathname)) {
        return false;
      }
    }

    if (uri2.origin !== "http://test") {
      if (
        uri1.protocol !== uri2.protocol ||
        uri1.origin !== uri2.origin ||
        !`${uri1.origin}${uri1.pathname}}`.includes(
          `${uri2.origin}${uri2.pathname}`,
        )
      ) {
        return false;
      }
    }

    let matching = true;
    uri2.searchParams.forEach((value, key) => {
      if (!uri1.searchParams.getAll(key).includes(value)) {
        matching = false;
      }
    });
    return matching;
  }
  return false;
}

export function createKeycloakTokenUrl(authority: string) {
  return `${authority}/protocol/openid-connect/token`;
}

export function createKeycloakLogoutUrl(authority: string) {
  return `${authority}/protocol/openid-connect/logout`;
}
